/**
 * Same-site auth bridge client.
 *
 * When the panel loads with no local Firebase session, this asks the store
 * origin (via a hidden iframe of mchosts.com.au/auth-bridge.html) for its
 * persisted Firebase Auth user and, if one exists, seeds it into this
 * origin's localStorage so the store login carries over without a second
 * sign-in. Works because both origins share the registrable domain
 * mchosts.com.au, making the iframe same-site (no storage partitioning).
 *
 * MUST run before Firebase Auth initialises (App.tsx initialises it at
 * module scope), because the SDK only reads persisted state once at init.
 * index.tsx therefore awaits this before dynamically importing App.
 *
 * One direction only (store to panel) and logout stays independent, by
 * design. Every failure path resolves silently so the user just sees the
 * normal login popup, exactly as before this feature existed.
 */
import { FIREBASE_CONFIG, STORE_URL } from "../config";

// The bridge page only ever replies to this exact origin, so running
// anywhere else (localhost, dev on pinti.family) is pointless. Skip.
const PANEL_ORIGIN = "https://control-panel.mchosts.com.au";

// localStorage key browserLocalPersistence reads at auth init (App.tsx:30).
const AUTH_KEY = `firebase:authUser:${FIREBASE_CONFIG.apiKey}:[DEFAULT]`;

const BRIDGE_TIMEOUT_MS = 4000;

/** Minimal shape check: the payload is only trusted if it looks like a
 * persisted Firebase user for OUR project (apiKey match) with a uid and a
 * refresh token. Anything else is dropped. */
function isValidUser(user: unknown): boolean {
  if (!user || typeof user !== "object") return false;
  const u = user as {
    apiKey?: unknown;
    uid?: unknown;
    stsTokenManager?: { refreshToken?: unknown };
  };
  return (
    u.apiKey === FIREBASE_CONFIG.apiKey &&
    typeof u.uid === "string" &&
    u.uid.length > 0 &&
    typeof u.stsTokenManager?.refreshToken === "string" &&
    u.stsTokenManager.refreshToken.length > 0
  );
}

export function runAuthBridge(): Promise<void> {
  return new Promise((resolve) => {
    // Already signed in on this origin, or storage unavailable: nothing to do.
    try {
      if (localStorage.getItem(AUTH_KEY)) {
        resolve();
        return;
      }
    } catch {
      resolve();
      return;
    }

    if (window.location.origin !== PANEL_ORIGIN) {
      resolve();
      return;
    }

    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    let settled = false;
    let timer = 0;

    const finish = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      window.removeEventListener("message", onMessage);
      iframe.remove();
      resolve();
    };

    const onMessage = (event: MessageEvent) => {
      // SECURITY: only accept the reply from the store origin, and only from
      // the exact iframe we created.
      if (event.origin !== STORE_URL) return;
      if (event.source !== iframe.contentWindow) return;
      const data = event.data;
      if (!data || data.type !== "MCHOSTS_AUTH_RESPONSE") return;

      if (isValidUser(data.user)) {
        try {
          localStorage.setItem(AUTH_KEY, JSON.stringify(data.user));
        } catch {
          // Storage full/unavailable: fall through to the normal login popup.
        }
      }
      finish();
    };

    timer = window.setTimeout(finish, BRIDGE_TIMEOUT_MS);
    window.addEventListener("message", onMessage);
    iframe.onload = () => {
      iframe.contentWindow?.postMessage(
        { type: "MCHOSTS_AUTH_REQUEST" },
        STORE_URL
      );
    };
    iframe.src = `${STORE_URL}/auth-bridge.html`;
    document.body.appendChild(iframe);
  });
}
